import React, { useMemo, useState } from "react";
import { buildClientFormData, isHtml, isRichTextEmpty, pickFormData } from "../../utils/helpers";
import { useFormik } from "formik";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { showToast } from "../../store/toast/toastSlice";
import { getClientAccountById, getClientById, updateClient } from "../../services/api/client";
import BaseButton from "../../components/ui/BaseButton";
import BaseModal from "../../components/ui/BaseModal";
import { ClientFormGrid, useClientFormContext } from "./CreateEditClient/CreateEditClientForm";

export const RichTextView = ({ value, className = "", emptyText = "-" }) => {
  if (isRichTextEmpty(value)) return <p className={`text-sm ${className}`}>{emptyText}</p>;

  if (!isHtml(value)) {
    return <p className={`text-sm whitespace-pre-wrap break-words ${className}`}>{value}</p>;
  }

  return (
    <div
      className={`ql-editor !min-h-0 !p-0 text-sm break-words ${className}`}
      dangerouslySetInnerHTML={{ __html: value }}
    />
  );
};

export const IncompleteBadge = () => (
  <span className="text-xs text-red-600 bg-red-50 rounded-full px-2 py-[2px]">Incomplete</span>
);

export const EditPencil = ({ onClick }) => (
  <i className="fa-light fa-pencil text-gray-900 cursor-pointer" onClick={onClick}></i>
);

export const ADDRESS_FIELDS = ["address", "city", "state", "zipCode", "country"];

// `save` is the mutation a section posts through; the servicing company's update by
// default, the customer's own entity route when the customer edits.
export const useClientSectionForm = ({ clientId, sections, schema, save = updateClient }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [editingSection, setEditingSection] = useState(null);

  const { data: clientData, isPending: isLoading } = useQuery({
    queryKey: ["client", clientId],
    queryFn: () => getClientById(clientId),
    enabled: !!clientId,
  });
  const client = clientData?.data;

  const { data: accountData } = useQuery({
    queryKey: ["clientAccount", clientId],
    queryFn: () => getClientAccountById(clientId, true),
    enabled: !!clientId,
  });
  const account = accountData?.data;

  const { isPending: isSaving, mutateAsync: saveClient } = useMutation({
    mutationFn: save,
  });

  const section = sections[editingSection];

  const sectionOfField = (field) =>
    Object.keys(sections).find((key) => sections[key].fields?.includes(field));

  const initialValues = useMemo(
    () => ({
      clientName: client?.clientName || "",
      manager: client?.managerName || "",
      email: client?.email || "",
      phone: client?.phoneNumber || "",
      teamEmail: client?.teamEmail || "",
      website: client?.website || "",
      companyAnnualRevenue: client?.companyAnnualRevenue || "",
      policies: client?.policies || "",
      address: client?.address?.addressLine1 || "",
      city: client?.address?.city || "",
      state: client?.address?.state || "",
      zipCode: client?.address?.zipCode || "",
      country: client?.address?.country || "",
      latitude: client?.address?.latitude || "",
      longitude: client?.address?.longitude || "",
      clientLogo: client?.clientLogo || null,
      registrationCertificate: client?.registrationCertificate || null,
      branchId: client?.branchId || null,
      teamId: client?.teamId || "",
      secondaryTeamId: client?.secondaryTeamId || "",
      accountsPerson: account?.accountsPerson || "",
      accountsEmail: account?.email || "",
      accountsPhone: account?.phone || "",
      paymentTerms: account?.paymentTerms || null,
      taxType: account?.taxType || null,
      taxPercentage: account?.taxPercentage || null,
      taxRegistrationNumber: client?.taxRegistrationNumber || "",
      registrationNumber: client?.registrationNumber || "",
    }),
    [client, account],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: section?.fields
      ? schema.pick(section.fields.filter((field) => schema.fields[field]))
      : undefined,
    onSubmit: async (values) => {
      try {
        const formData = pickFormData(buildClientFormData(values), section?.fields);
        await saveClient({ id: clientId, formData });
        dispatch(showToast({ type: "success", message: "Details updated successfully" }));
        await queryClient.invalidateQueries({ queryKey: ["client", clientId] });
        await queryClient.invalidateQueries({ queryKey: ["clientAccount", clientId] });
        setEditingSection(null);
      } catch (error) {
        dispatch(
          showToast({
            type: "error",
            message: error?.response?.data?.message || "Something went wrong. Please try again.",
          }),
        );
      }
    },
  });

  const closeSection = () => {
    formik.resetForm();
    setEditingSection(null);
  };

  // Cards whose saved values fail a rule, so the page can flag them before anyone edits.
  const incompleteSections = useMemo(() => {
    if (!client) return new Set();
    try {
      schema.validateSync(initialValues, { abortEarly: false });
      return new Set();
    } catch (error) {
      return new Set(
        (error.inner || []).map((issue) => sectionOfField(issue.path)).filter(Boolean),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, initialValues, schema]);

  return {
    client,
    account,
    formik,
    isLoading,
    isSaving,
    editingSection,
    section,
    openSection: setEditingSection,
    closeSection,
    submitSection: formik.handleSubmit,
    incompleteSections,
  };
};

// A section is { title, fields, Fields }: `fields` names what it validates, `Fields`
// renders its inputs. `children` replaces the inputs for sections with their own editor.
export const SectionEditModal = ({ sectionForm, formProps = {}, children }) => {
  const { section, formik, isSaving, closeSection, submitSection } = sectionForm;
  const ctx = useClientFormContext({ formik, ...formProps });
  if (!section) return null;
  const { Fields } = section;

  return (
    <BaseModal
      className="-top-[200px] overflow-auto"
      open
      close={closeSection}
      style={{ width: "1060px" }}
    >
      <div className="py-4 px-8">
        <div className="flex justify-between items-center border-b-2 pb-1">
          <h5 className="text-dark font-normal">{section.title}</h5>
        </div>
      </div>

      <div className="px-4 pb-4">
        {children ||
          (Fields && (
            <ClientFormGrid ctx={ctx} compactTop>
              <Fields ctx={ctx} />
            </ClientFormGrid>
          ))}
      </div>

      <div
        className="d-flex content-center bg-white py-6"
        style={{ boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)" }}
      >
        <BaseButton
          variant="outlined"
          color="primary"
          className="px-[28px] py-[5px] bg-light text-primary hover:text-primary"
          style={{ marginRight: "16px" }}
          disabled={isSaving}
          onClick={closeSection}
        >
          Cancel
        </BaseButton>
        <BaseButton
          loaderSize="22px"
          isLoading={isSaving}
          color="primary"
          className="py-[5px] px-[38px]"
          disabled={isSaving || !formik.dirty}
          onClick={submitSection}
        >
          Update
        </BaseButton>
      </div>
    </BaseModal>
  );
};
